import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError, WHATSAPP_URL } from "@/lib/api";
import { SocialLinks } from "@/components/SocialLinks";

const SERVICES = ["Redes sociales", "Anuncios", "Web o tienda online", "Asistente IA / WhatsApp", "Estrategia completa", "Aún no lo sé"];
const EMPTY = { name: "", email: "", phone: "", business_type: "", service: SERVICES[0], message: "" };

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} data-testid={`quote-${id}-input`} className="h-11 bg-white" {...props} />
  </div>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setSent(true);
      setForm(EMPTY);
      toast.success("¡Solicitud enviada! Te escribimos en menos de 24 h.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="bg-[#1E1A17] text-[#FAF7F2]">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-28">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#F4A261]">Contacto</p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Cuéntanos de tu negocio y te enviamos una propuesta</h2>
          <p className="mt-4 text-[#FAF7F2]/70">Sin compromiso. Respondemos en menos de 24 horas laborables.</p>
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#FAF7F2]/20 px-5 py-3 text-sm font-semibold transition-colors hover:bg-[#25D366] hover:text-[#1E1A17]">
            <MessageCircle className="h-4 w-4" /> Prefiero escribir por WhatsApp
          </a>
          <p className="mt-10 text-xs font-semibold uppercase tracking-wider text-[#FAF7F2]/60">Síguenos en redes</p>
          <div className="mt-3"><SocialLinks light withLabel /></div>
        </div>
        <div className="rounded-2xl bg-[#FAF7F2] p-6 text-[#1E1A17] sm:p-8 lg:col-span-7">
          {sent ? (
            <div data-testid="quote-success" className="flex min-h-[360px] flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-14 w-14 text-[#2A9D8F]" />
              <h3 className="mt-4 text-2xl font-bold">¡Gracias! Ya tenemos tu solicitud</h3>
              <p className="mt-2 text-[#6E655F]">Te contactaremos muy pronto.</p>
              <Button variant="outline" onClick={() => setSent(false)} className="mt-6 rounded-full" data-testid="quote-new-button">Enviar otra</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
              <Field id="name" label="Nombre" required value={form.name} onChange={set("name")} />
              <Field id="email" label="Email" type="email" required value={form.email} onChange={set("email")} />
              <Field id="phone" label="Teléfono / WhatsApp" value={form.phone} onChange={set("phone")} />
              <Field id="business" label="Tipo de negocio" placeholder="Ej.: peluquería" value={form.business_type} onChange={set("business_type")} />
              <div className="space-y-2 sm:col-span-2">
                <Label>Servicio de interés</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="quote-service-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="message">Mensaje</Label>
                <Textarea id="message" data-testid="quote-message-input" rows={4} className="bg-white" placeholder="¿Qué te gustaría conseguir?" value={form.message} onChange={set("message")} />
              </div>
              <Button type="submit" disabled={loading} data-testid="quote-form-submit" className="h-12 rounded-full bg-[#E05A47] text-base hover:bg-[#c94a38] sm:col-span-2">
                {loading ? "Enviando…" : "Pedir mi cotización"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
